import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { Row, Col, Form, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { ButtonComponent } from "../../../components/elements";
import Pagination from 'react-bootstrap/Pagination';
import Select from 'react-select';
import { FiPlus, FiEdit, FiEye, FiUser, FiSearch, FiX } from "react-icons/fi";


export default function PurchaseIndex() {
    const { t } = useContext(TranslatorContext);
    const [purchases, setPurchases] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState('');
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0
    });

    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [filters, setFilters] = useState({
        vendor_id: '',
        purchase_number: '',
        status: '',
        start_date: getTodayDate(),
        end_date: getTodayDate()
    });

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    const role = localStorage.getItem("role");
    const isAdmin = role?.toLowerCase() === 'admin';

    const navigate = useNavigate();

    const fetchVendors = async () => {
        try {
            const token = localStorage.getItem("authToken");
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/purchase-vendors`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setVendors(res.data.vendors || []);
        } catch (err) {
            console.error("Error fetching vendors:", err);
        }
    };

    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func(...args);
            }, delay);
        };
    };

    const debouncedPurchaseSearch = useCallback(
        debounce((value) => {
            setFilters(prev => ({ ...prev, purchase_number: value }));
        }, 800),
        []
    );

    const handlePurchaseNumberChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        debouncedPurchaseSearch(value);
    };

    const fetchPurchases = async (page = 1) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");

            const params = new URLSearchParams({
                page: page,
                per_page: pagination.per_page,
                ...(filters.vendor_id && { vendor_id: filters.vendor_id }),
                ...(filters.purchase_number && { purchase_number: filters.purchase_number }),
                ...(filters.status && { status: filters.status }),
                ...(filters.start_date && { start_date: filters.start_date }),
                ...(filters.end_date && { end_date: filters.end_date })
            });

            const res = await axios.get(`${process.env.REACT_APP_API_URL}/purchases?${params}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setPurchases(res.data.purchase.data || []);
            setPagination({
                current_page: res.data.purchase.current_page,
                last_page: res.data.purchase.last_page,
                per_page: res.data.purchase.per_page,
                total: res.data.purchase.total
            });
        } catch (err) {
            console.error("Error fetching purchases:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVendors();
    }, []);

    useEffect(() => {
        fetchPurchases(1);
    }, [filters.vendor_id, filters.purchase_number, filters.status, filters.start_date, filters.end_date, pagination.per_page]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleVendorChange = (selectedOption) => {
        setFilters(prev => ({
            ...prev,
            vendor_id: selectedOption ? selectedOption.value : '',
            start_date: null,
            end_date: null,
        }));
    };

    const handlePageChange = (page) => {
        fetchPurchases(page);
    };

    const clearFilters = () => {
        setSearchInput('');
        setFilters({
            vendor_id: '',
            purchase_number: '',
            status: '',
            start_date: getTodayDate(),
            end_date: getTodayDate()
        });
    };

    const renderPaginationItems = () => {
        const items = [];
        const currentPage = pagination.current_page;
        const lastPage = pagination.last_page;

        items.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
            />
        );

        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(lastPage, currentPage + 2);

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
            }
        }

        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        if (endPage < lastPage) {
            if (endPage < lastPage - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
            }
            items.push(
                <Pagination.Item key={lastPage} onClick={() => handlePageChange(lastPage)}>
                    {lastPage}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === lastPage}
            />
        );

        return items;
    };

    const vendorOptions = [
        { value: '', label: t('All Vendors') },
        ...vendors.map(vendor => ({
            value: vendor.id,
            label: vendor.company_name
        }))
    ];

    const selectedVendorOption = vendorOptions.find(option => option.value === filters.vendor_id) || vendorOptions[0];

    if (loading) return <PageLayout><p>{t("loading")}...</p></PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t('purchases')}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('purchases')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {isAdmin && (
                    <Col xl={12}>
                        <div className="mc-card p-3 mb-3">
                            <Row className="align-items-end">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>{t('Purchase Number')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="purchase_number"
                                            value={searchInput}
                                            onChange={handlePurchaseNumberChange}
                                            placeholder={t('Search by purchase number...')}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>{t('Vendor')}</Form.Label>
                                        <Select
                                            name="vendor_id"
                                            options={vendorOptions}
                                            value={selectedVendorOption}
                                            onChange={handleVendorChange}
                                            placeholder={t('Select vendor...')}
                                            isClearable={true}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label>{t('Start Date')}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="start_date"
                                            value={filters.start_date}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label>{t('End Date')}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="end_date"
                                            value={filters.end_date}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={2} className="text-end">
                                    <ButtonComponent
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={clearFilters}
                                    >
                                        {t('Clear All Filters')}
                                    </ButtonComponent>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                )}

                <Col xl={12}>
                    <div className="mc-card">
                        <div className="table-responsive-wrapper">
                            <Table responsive className="table mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>{t('purchase number')}</th>
                                        <th>{t('vendor')}</th>
                                        <th>{t('purchase date')}</th>
                                        <th>{t('Products')}</th>
                                        <th>{t('amount')}</th>
                                        <th>{t('Action')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {purchases.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">
                                                {t('No purchases found')}
                                            </td>
                                        </tr>
                                    ) : (
                                        purchases.map((purchase, index) => (
                                            <tr key={purchase.id}>
                                                <td>{purchase.id ?? '-'}</td>
                                                <td>
                                                    <strong>{purchase.purchase_number}</strong>
                                                </td>
                                                <td>
                                                    <span
                                                        style={{ cursor: "pointer", color: "#0d6efd", fontWeight: "500" }}
                                                        onClick={() =>
                                                            setFilters(prev => ({
                                                                ...prev,
                                                                vendor_id: purchase.vendor_id,
                                                                start_date: '',
                                                                end_date: ''
                                                            }))
                                                        }
                                                    >
                                                        {purchase.vendor_name || '-'}
                                                    </span>
                                                </td>
                                                <td>{purchase.purchase_date}</td>
                                                <td>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {purchase.purchase_summary?.slice(0, 3).map((item, i) => (
                                                            <span key={i} className="badge bg-light text-dark">
                                                                {item.name} × {item.qty}
                                                            </span>
                                                        ))}
                                                        {purchase.purchase_summary?.length > 5 && (
                                                            <span className="badge bg-secondary">
                                                                +{purchase.purchase_summary.length - 5} more
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="fw-bold">
                                                        {purchase.total_amount?.toLocaleString()}
                                                    </span>
                                                </td>

                                                <td>
                                                    <div className="mc-table-action">
                                                        {hasPermission("Purchases edit") && (
                                                            <ButtonComponent
                                                                type="button"
                                                                className="material-icons edit"
                                                                onClick={() => navigate(`/purchase-edit/${purchase.id}`)}
                                                                title="Edit"
                                                            >
                                                                edit
                                                            </ButtonComponent>
                                                        )}
                                                        <ButtonComponent
                                                            type="button"
                                                            className="material-icons view"
                                                            onClick={() => navigate(`/vendor-show/${purchase.vendor_id}`)}
                                                            title="View"
                                                        >
                                                            <FiEye />
                                                        </ButtonComponent>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {pagination.last_page > 1 && (
                            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                <div className="text-muted small">
                                    {t('Showing')} {((pagination.current_page - 1) * pagination.per_page) + 1}
                                    {t(' to ')} {Math.min(pagination.current_page * pagination.per_page, pagination.total)}
                                    {t(' of ')} {pagination.total} {t('entries')}
                                </div>
                                <Pagination>
                                    {renderPaginationItems()}
                                </Pagination>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}